import React, { CSSProperties, ReactNode, useEffect, useState } from "react";
import {
  AppBar,
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Slide,
  Toolbar,
  Typography,
} from "@mui/material";
import { DialogProps } from "@mui/material/Dialog/Dialog";
import useIsMobile from "../hooks/useIsMobile";
import { TransitionProps } from "@mui/material/transitions";
import CloseIcon from "@mui/icons-material/Close";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type HeaderProps = {
  primaryActionEnabled: boolean;
  handleClose: () => void;
  title?: string;
  primaryAction?: () => void;
  primaryActionName?: string | ReactNode;
  unpad?: boolean;
  unpadHeaderBottomOnly?: boolean;
};

const FullScreenHeader = ({
  title,
  handleClose,
  primaryAction,
  primaryActionName,
  primaryActionEnabled,
}: HeaderProps) => {
  let action: ReactNode = null;
  if (primaryAction && typeof primaryActionName === "string") {
    action = (
      <Button
        disabled={!primaryActionEnabled}
        onClick={primaryAction}
        variant="text"
        sx={{ color: "#FFF" }}
      >
        {primaryActionName}
      </Button>
    );
  } else if (primaryAction && primaryActionName) {
    action = (
      <IconButton disabled={!primaryActionEnabled} onClick={primaryAction}>
        {primaryActionName}
      </IconButton>
    );
  }
  return (
    <AppBar sx={{ position: "relative", backgroundColor: "#575F70" }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <Typography
          sx={{
            ml: 2,
            flex: 1,
            textOverflow: "ellipsis",
            overflowX: "hidden",
            textWrap: "nowrap",
          }}
          variant="h6"
          component="div"
        >
          {title}
        </Typography>
        {action}
      </Toolbar>
    </AppBar>
  );
};

const NormalHeader = ({
  title,
  handleClose,
  unpad,
  unpadHeaderBottomOnly,
  hideCloseButton,
}: HeaderProps & { hideCloseButton?: boolean }) => {
  return (
    <>
      {title && (
        <DialogTitle
          sx={{
            textOverflow: "ellipsis",
            overflowX: "hidden",
            textWrap: "nowrap",
            paddingRight: "40px",
            ...(unpadHeaderBottomOnly ? { paddingBottom: "0" } : {}),
            ...(!unpadHeaderBottomOnly && unpad
              ? { paddingBottom: "0", paddingTop: "0" }
              : {}),
          }}
        >
          {title}
        </DialogTitle>
      )}
      {!hideCloseButton && (
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </>
  );
};

interface Props extends DialogProps {
  onClose: () => void;
  title?: string;
  primaryAction?: () => void;
  primaryActionName?: string | ReactNode;
  primaryActionColor?: "error" | "info" | "primary";
  noActionsFullScreen?: boolean;
  primaryActionEnabled?: boolean;
  isNewDesign?: boolean;
  fullWidthSize?: Breakpoint;
  unpad?: boolean;
  unpadHeaderBottomOnly?: boolean;
  style?: CSSProperties;
  hideCloseButton?: boolean;
  noHeader?: boolean;
}

export default ({
  onClose,
  title,
  primaryAction,
  primaryActionName,
  children,
  fullScreen,
  noActionsFullScreen,
  fullWidthSize,
  primaryActionEnabled = true,
  unpad,
  unpadHeaderBottomOnly,
  style,
  primaryActionColor,
  hideCloseButton = false,
  noHeader = false,
  ...otherProps
}: Props) => {
  const isMobile = useIsMobile();
  // Defer to an explicit fullScreen prop if it's provided
  const isFullScreen = fullScreen === undefined ? isMobile : fullScreen;
  const headerProps = {
    title,
    handleClose: onClose,
    primaryAction,
    primaryActionName,
    primaryActionEnabled,
    unpad,
    unpadHeaderBottomOnly,
  };
  const [themeBase] = useState(
    document.querySelector("meta[name=theme-color]")?.getAttribute("content") ||
      undefined,
  );

  const revertThemeColor = () => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", themeBase || "");
    }
  };
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (otherProps.open) {
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", "#575F70");
      }
    } else {
      revertThemeColor();
    }
    return () => {
      revertThemeColor();
    };
  }, [otherProps.open]);

  return (
    <Dialog
      fullScreen={isFullScreen}
      PaperComponent={Paper}
      TransitionComponent={isFullScreen ? Transition : undefined}
      slotProps={{
        backdrop: {
          style: {
            background: "rgba(209, 209, 209, 0.30)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
      PaperProps={{
        style: {
          ...(isFullScreen
            ? {
                boxShadow: "none",
              }
            : {
                borderRadius: "12px",
                backgroundColor: "#FFF",
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                border: "1px solid #EAECF0",
              }),
          ...style,
        },
      }}
      onClose={onClose}
      maxWidth={fullWidthSize || undefined}
      fullWidth={fullWidthSize ? true : undefined}
      {...otherProps}
    >
      {!noHeader &&
        (isFullScreen ? (
          <FullScreenHeader {...headerProps} />
        ) : (
          <NormalHeader {...headerProps} hideCloseButton={hideCloseButton} />
        ))}
      <DialogContent sx={{ ...(unpad ? { padding: "0px 24px" } : {}) }}>
        {children}
      </DialogContent>
      {!isFullScreen &&
        !noActionsFullScreen &&
        primaryAction &&
        primaryActionName && (
          <DialogActions sx={{ padding: "24px" }}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!primaryActionEnabled}
              onClick={primaryAction}
              color={primaryActionColor}
            >
              {primaryActionName}
            </Button>
          </DialogActions>
        )}
    </Dialog>
  );
};
